/**
 * Stitches screenshots of a page that is being scrolled into one tall image, entirely on the device.
 *
 * A browser only lets a site see what is on screen, so it can not read or scroll another page. This is the honest
 * alternative: the person scrolls the shared tab, the tool grabs frames, and each new frame is lined up with the
 * previous one by finding the vertical shift at which their rows match. Rows that do not move (a sticky header or footer)
 * are found and kept once. Only downward scrolling adds anything; a frame it can not line up with is reported, never guessed.
 */

export interface RgbaFrame { data: Uint8ClampedArray | Uint8Array; width: number; height: number }

export type AddStatus =
  | "first"    // first frame kept
  | "added"    // new rows appended
  | "same"     // nothing moved
  | "up"       // scrolled upward or back to something already captured: nothing added
  | "lost"     // moved, but no confident match (scrolled too far between frames, or the content changed)
  | "ambiguous" // several equally good shifts (repeating content or large blank stretches)
  | "resized"  // the shared window changed size: frame ignored
  | "limit";   // the image is as tall as it is allowed to get

export interface AddResult { status: AddStatus; dy?: number }

const COLS = 96;           // cells per row in the row signature
const S = COLS * 2;        // each cell keeps its darkest and its lightest pixel, so light-on-dark pages work too
const MATCH = 12;          // a row "matches" when its mean signature difference is at most this (0-255 scale)
const EQUAL = 3;           // rows "are the same" (static band) at this tolerance
export const MAX_STITCH_HEIGHT = 16000; // px; well under every browser's canvas limit

/** One signature per row: the darkest and lightest pixel in each of COLS cells, so thin text strokes are not averaged away. */
export function rowSignatures(f: RgbaFrame): Uint8Array {
  const { width: W, height: H, data } = f;
  const sig = new Uint8Array(H * S);
  const x0s = new Int32Array(COLS), x1s = new Int32Array(COLS);
  for (let k = 0; k < COLS; k++) { x0s[k] = Math.floor((k * W) / COLS); x1s[k] = Math.max(x0s[k] + 1, Math.floor(((k + 1) * W) / COLS)); }
  for (let y = 0; y < H; y++) {
    const row = y * W * 4;
    for (let k = 0; k < COLS; k++) {
      let min = 255, max = 0;
      for (let x = x0s[k]; x < x1s[k]; x++) {
        const p = row + x * 4;
        const l = (data[p] * 77 + data[p + 1] * 151 + data[p + 2] * 28) >> 8;
        if (l < min) min = l;
        if (l > max) max = l;
      }
      sig[y * S + k] = min;
      sig[y * S + COLS + k] = max;
    }
  }
  return sig;
}

const rowDiff = (a: Uint8Array, i: number, b: Uint8Array, j: number) => {
  let s = 0;
  for (let k = 0; k < S; k++) s += Math.abs(a[i * S + k] - b[j * S + k]);
  return s / S;
};
const informative = (s: Uint8Array, i: number) => {
  let lo = 255, hi = 0;
  for (let k = 0; k < S; k++) { const v = s[i * S + k]; if (v < lo) lo = v; if (v > hi) hi = v; }
  return hi - lo >= 24;
};

/** Rows at the top and bottom that sit in the same place in both frames (a sticky header or footer). */
export function staticBands(a: Uint8Array, b: Uint8Array, H: number): { top: number; bottom: number } {
  let top = 0;
  while (top < H && rowDiff(a, top, b, top) <= EQUAL) top++;
  if (top === H) return { top: H, bottom: 0 };
  let bottom = 0;
  while (bottom < H - top && rowDiff(a, H - 1 - bottom, b, H - 1 - bottom) <= EQUAL) bottom++;
  return { top, bottom };
}

/**
 * How far B is scrolled down from A, within rows [rs, re): B row r shows what A row r+dy showed.
 * null (with a reason) when no shift matches well, or when two unrelated shifts match equally well.
 */
export function findShift(A: Uint8Array, B: Uint8Array, rs: number, re: number, prevDy?: number): { dy: number } | { fail: "lost" | "ambiguous" } {
  const reg = re - rs;
  const minOverlap = Math.max(20, Math.floor(reg * 0.15));
  const maxDy = reg - minOverlap;
  const info: number[] = [];
  for (let r = rs; r < re; r++) if (informative(B, r)) info.push(r);
  if (info.length < 6 || maxDy < 1) return { fail: "lost" };
  const probes = info.length <= 48 ? info : Array.from({ length: 48 }, (_, i) => info[Math.floor((i * info.length) / 48)]);
  const scores: number[] = new Array(maxDy + 1).fill(Infinity);
  for (let dy = 1; dy <= maxDy; dy++) {
    let sum = 0, n = 0;
    for (const r of probes) { if (r + dy >= re) break; sum += Math.min(rowDiff(B, r, A, r + dy), 40); n++; }
    if (n >= Math.min(6, probes.length)) scores[dy] = sum / n;
  }
  // Mean difference over every informative row of the overlap: the exact answer among near-equal neighbours.
  const dense = (dy: number) => { let sum = 0, n = 0; for (const r of info) { if (r + dy >= re) break; sum += Math.min(rowDiff(B, r, A, r + dy), 40); n++; } return n >= 6 ? sum / n : Infinity; };
  const refine = (dy: number) => { let b = dy, bs = Infinity; for (let d = Math.max(1, dy - 3); d <= Math.min(maxDy, dy + 3); d++) { const sc = dense(d); if (sc < bs) { bs = sc; b = d; } } return b; };
  let best = 1;
  for (let dy = 2; dy <= maxDy; dy++) if (scores[dy] < scores[best]) best = dy;
  best = refine(best);
  if (!(scores[best] <= MATCH)) return { fail: "lost" };
  // Several clearly different shifts that fit equally well: repeating content or blank stretches. Prefer the one that continues the scroll speed.
  const ties: number[] = [];
  for (let dy = 1; dy <= maxDy; dy++) if (Math.abs(dy - best) > 3 && scores[dy] <= scores[best] + 0.5) ties.push(dy);
  let pick = best;
  if (ties.length > 0) {
    if (prevDy === undefined) return { fail: "ambiguous" };
    const all = [best, ...ties];
    pick = refine(all.reduce((p, c) => (Math.abs(c - prevDy) < Math.abs(p - prevDy) ? c : p)));
    if (Math.abs(pick - prevDy) > Math.max(30, prevDy * 0.5)) return { fail: "ambiguous" };
  }
  // dense check over every informative row in the overlap
  let ok = 0, tot = 0;
  for (const r of info) { if (r + pick >= re) break; tot++; if (rowDiff(B, r, A, r + pick) <= MATCH) ok++; }
  if (tot < 6 || ok / tot < 0.85) return { fail: "lost" };
  return { dy: pick };
}

export class ScrollStitcher {
  // Everything is kept in "global" rows: row 0 is the top of the first frame, and a frame scrolled by `offset`
  // shows global rows offset..offset+h. Strips are appended in order, so rows are never repeated or skipped.
  private strips: Uint8ClampedArray[] = [];
  private end = 0;      // global row up to which strips hold pixels
  private offset = 0;   // how far the newest kept frame is scrolled from the first
  private w = 0;
  private h = 0;
  private last: RgbaFrame | null = null;
  private lastSig: Uint8Array | null = null;
  private lastBottom = 0;
  private prevDy: number | undefined;
  private frames = 0;

  constructor(private maxHeight = MAX_STITCH_HEIGHT) {}

  get height() { return this.last ? (this.frames > 1 ? this.offset + this.h : this.h) : 0; }
  get frameCount() { return this.frames; }

  private rows(f: RgbaFrame, from: number, to: number) {
    const base = f.data.byteOffset + from * this.w * 4;
    return new Uint8ClampedArray(f.data.buffer.slice(base, base + (to - from) * this.w * 4));
  }

  add(frame: RgbaFrame): AddResult {
    if (!this.last || !this.lastSig) {
      this.w = frame.width; this.h = frame.height;
      this.last = frame; this.lastSig = rowSignatures(frame);
      this.strips = [this.rows(frame, 0, frame.height)];
      this.end = frame.height; this.offset = 0; this.frames = 1;
      return { status: "first" };
    }
    if (frame.width !== this.w || frame.height !== this.h) return { status: "resized" };
    const sig = rowSignatures(frame);
    const { top, bottom } = staticBands(this.lastSig, sig, this.h);
    if (top >= this.h || this.h - top - bottom < Math.max(40, this.h * 0.1)) return { status: "same" };
    const rs = top, re = this.h - bottom;
    const found = findShift(this.lastSig, sig, rs, re, this.prevDy);
    if ("fail" in found) {
      // Scrolled back up over ground that is already kept? Then the earlier frame lines up with this one in reverse.
      const rev = findShift(sig, this.lastSig, rs, re);
      if ("dy" in rev) return { status: "up", dy: rev.dy };
      return { status: found.fail };
    }
    const dy = found.dy;
    if (this.offset + dy + this.h > this.maxHeight) return { status: "limit" };
    if (this.frames === 1) {
      // The first frame's own footer rows are not scrolling content: they come back once, from the newest frame, at the end.
      this.end = this.h - bottom;
      this.strips = [this.rows(this.last, 0, this.end)];
    }
    this.offset += dy;
    const newEnd = this.offset + re;
    if (newEnd > this.end) { this.strips.push(this.rows(frame, this.end - this.offset, re)); this.end = newEnd; }
    this.lastBottom = bottom;
    this.prevDy = dy;
    this.frames++;
    this.last = frame; this.lastSig = sig;
    return { status: "added", dy };
  }

  /** The stitched image, or null before the first frame. A single frame comes back unchanged. */
  result(): RgbaFrame | null {
    if (!this.last) return null;
    if (this.frames === 1) return { data: this.strips[0], width: this.w, height: this.h };
    const from = Math.max(this.h - this.lastBottom, this.end - this.offset); // newest frame's fixed footer, minus anything already taken
    const parts = [...this.strips, this.rows(this.last, from, this.h)];
    const total = parts.reduce((n, p) => n + p.length, 0);
    const out = new Uint8ClampedArray(total);
    let off = 0;
    for (const p of parts) { out.set(p, off); off += p.length; }
    return { data: out, width: this.w, height: total / (this.w * 4) };
  }
}
